import { ModalTypes } from 'enums/modalTypes';
import { closeModal } from 'graphql/modals';
import { useEffect } from 'react';
import {
  Button,
  IconType,
  Modal,
  ModalSize,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { useGetSoftSkillContentBlob } from './DownloadSoftSkillContentModal.helpers';
import styles from './DownloadSoftSkillContentModal.module.scss';

export const DownloadSoftSkillContentModal = () => {
  const { handleMsgType } = useNotification();
  const { blobUrl, loading, hasError, retry, getBlobUrl } =
    useGetSoftSkillContentBlob();

  const close = () => {
    closeModal(ModalTypes.DOWNLOAD_SOFT_SKILLS_CONTENT_CSV_MODAL);
  };

  useEffect(() => {
    getBlobUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadSoftSkillContent = () => {
    if (blobUrl) {
      window.open(blobUrl);
      handleMsgType({
        title: 'Successfully downloaded file!',
        type: TNotification.success,
      });
    }
  };

  return (
    <Modal
      onClose={close}
      isOpen
      modalSize={ModalSize.SMALL}
      header={hasError ? 'Export failed' : 'Export content'}
      headerIconType={hasError ? IconType.ERROR : undefined}
    >
      <Content
        hasError={hasError}
        loading={loading}
        onClose={close}
        onDownload={handleDownloadSoftSkillContent}
        onTryAgain={retry}
      />
    </Modal>
  );
};

type ContentProps = {
  hasError: boolean;
  loading: boolean;
  onClose: () => void;
  onDownload: () => void;
  onTryAgain: () => void;
};

function Content({
  hasError,
  loading,
  onClose,
  onDownload,
  onTryAgain,
}: ContentProps) {
  if (hasError) {
    return (
      <div className={styles.errorContent}>
        <div className={styles.main}>
          <p className={styles.mainText}>
            Failed to export soft skills content.
          </p>
          <p className={styles.mainText}>
            Please try again. If this problem persists, please contact the
            Spotted Zebra technical team.
          </p>
        </div>
        <div className={styles.actions}>
          <Button size="medium" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button size="medium" onClick={onTryAgain}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.content}>
      <p className={styles.mainText}>
        {loading
          ? 'Your export is being prepared.'
          : 'Your CSV file is ready for download.'}
      </p>
      <div className={styles.actions}>
        <Button size="medium" variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button size="medium" onClick={onDownload} disabled={loading} loading={loading}>
          {loading ? 'Preparing' : 'Download'}
        </Button>
      </div>
    </div>
  );
}
